import React, { useState } from 'react';
import { Calendar, Clock, ArrowRight, Tag, User } from 'lucide-react';
import { blogPosts } from '../data/mock';

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  const BlogModal = ({ post, onClose }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[rgb(26,28,30)] rounded-2xl border border-[rgba(255,255,255,0.1)] max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">{post.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-[rgb(161,161,170)]">
                <div className="flex items-center space-x-1">
                  <User size={14} />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar size={14} />
                  <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock size={14} />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] text-2xl ml-4"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="prose prose-invert max-w-none">
            <div className="text-[rgb(218,218,218)] leading-relaxed whitespace-pre-line">
              {post.content}
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.1)]">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[rgb(218,255,1)]/10 text-[rgb(218,255,1)] rounded-lg text-sm font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="blog" className="py-20 bg-[rgb(26,28,30)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              AI/ML <span className="text-[rgb(218,255,1)]">Insights</span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              Sharing knowledge, experiences, and lessons learned from building AI/ML systems
            </p>
          </div>

          {/* Featured Post */}
          <div className="mb-16">
            {blogPosts.filter(post => post.featured).map((post) => (
              <div
                key={post.id}
                className="group bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl overflow-hidden hover:border-[rgb(218,255,1)] hover:transform hover:translate-y-[-4px] transition-all duration-300 relative cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                {/* Top accent border */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="p-8">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="px-3 py-1 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] rounded-lg text-sm font-medium">
                      Featured
                    </span>
                    <span className="px-3 py-1 bg-[rgb(38,40,42)] text-[rgb(218,218,218)] rounded-lg text-sm">
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                    {post.title}
                  </h3>
                  
                  <p className="text-[rgb(161,161,170)] text-lg mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-[rgb(161,161,170)]">
                      <div className="flex items-center space-x-1">
                        <User size={14} />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-[rgb(218,255,1)] group-hover:translate-x-2 transition-transform duration-200">
                      <span className="font-medium">Read More</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-6">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[rgb(38,40,42)] text-[rgb(218,218,218)] rounded-lg text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Other Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.filter(post => !post.featured).map((post) => (
              <div
                key={post.id}
                className="group bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 hover:border-[rgb(218,255,1)] hover:transform hover:translate-y-[-4px] transition-all duration-300 relative cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                {/* Top accent border */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="flex items-center space-x-2 mb-4">
                  <span className="px-3 py-1 bg-[rgb(38,40,42)] text-[rgb(218,218,218)] rounded-lg text-sm">
                    {post.readTime}
                  </span>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                  {post.title}
                </h3>
                
                <p className="text-[rgb(161,161,170)] mb-4 leading-relaxed">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-sm text-[rgb(161,161,170)] mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <User size={14} />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.slice(0, 2).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-[rgb(38,40,42)] text-[rgb(218,218,218)] rounded text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center space-x-2 text-[rgb(218,255,1)] group-hover:translate-x-2 transition-transform duration-200">
                  <span className="font-medium text-sm">Read Article</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-white mb-4">
                More Content <span className="text-[rgb(218,255,1)]">Coming Soon</span>
              </h3>
              <p className="text-[rgb(161,161,170)] mb-6 max-w-2xl mx-auto">
                I regularly share insights about AI/ML projects, technical tutorials, and lessons learned 
                from building production systems. Subscribe to stay updated!
              </p>
              <button className="bg-[rgb(218,255,1)] hover:bg-[rgb(166,190,21)] text-[rgb(17,17,19)] px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:transform hover:translate-y-[-2px]">
                Subscribe for Updates
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Modal */}
      {selectedPost && <BlogModal post={selectedPost} onClose={() => setSelectedPost(null)} />}
    </section>
  );
};

export default Blog;